"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import type { Id } from "@agroproj/backend/convex/_generated/dataModel";
import { documentChatRequest, type DocumentChatRequest } from "@agroproj/backend/convex/assistant/documentChat";
import { FEATURE_FLAG_KEYS } from "@agroproj/backend/convex/constants/featureFlags";
import { usePaginatedQuery, useQuery } from "convex/react";
import { FilePlusIcon, PaperclipIcon, XIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useProjectDocumentUpload } from "@/components/projects/details/use-project-document-upload";
import { CusteioDocumentDraft } from "@/components/projects/new/custeio-document-draft";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useConvexAuthReady } from "@/hooks/use-convex-auth-ready";
import { useConvexFeatureFlag } from "@/hooks/use-convex-feature-flag";
import { AssistantProposalBoundary } from "./assistant-proposal-boundary";
export type DocumentChatContext = {
  request?: DocumentChatRequest;
  blocked: boolean;
};
export function AssistantDocuments(t0) {
  const $ = _c(31);
  const {
    currentProjectId,
    organizationSlug,
    disabled,
    onChange
  } = t0;
  const [open, setOpen] = useState(false);
  const [draftOpen, setDraftOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [projectId, setProjectId] = useState(currentProjectId);
  const {
    canQuery
  } = useConvexAuthReady();
  const {
    enabled
  } = useConvexFeatureFlag(FEATURE_FLAG_KEYS.DOCUMENT_ASSISTANCE);
  let t1;
  if ($[0] !== canQuery || $[1] !== open || $[2] !== projectId || $[3] !== search) {
    t1 = canQuery && open && !projectId && search.trim().length >= 2 ? {
      search: search.trim(),
      limit: 5
    } : "skip";
    $[0] = canQuery;
    $[1] = open;
    $[2] = projectId;
    $[3] = search;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const projects = useQuery(api.assistant.queries.searchProjects, t1);
  let t2;
  let t3;
  if ($[5] !== onChange || $[6] !== open || $[7] !== projectId) {
    t2 = () => {
      if (!open || !projectId) {
        onChange({
          blocked: false
        });
      }
    };
    t3 = [open, projectId, onChange];
    $[5] = onChange;
    $[6] = open;
    $[7] = projectId;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  if ($[10] !== open) {
    t4 = () => setOpen(!open);
    $[10] = open;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <PaperclipIcon />;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== disabled || $[14] !== open || $[15] !== t4) {
    t6 = <Button size="sm" variant="ghost" disabled={disabled} onClick={t4} aria-expanded={open}>{t5}Documentos</Button>;
    $[13] = disabled;
    $[14] = open;
    $[15] = t4;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== currentProjectId || $[18] !== disabled || $[19] !== draftOpen || $[20] !== enabled || $[21] !== onChange || $[22] !== open || $[23] !== organizationSlug || $[24] !== projectId || $[25] !== projects || $[26] !== search) {
    t7 = open ? <section aria-label="Documentos da conversa" className="space-y-3 border-t pt-3">{!enabled ? <p>A leitura de documentos está desativada para esta organização.</p> : <>{!projectId ? <><Input label="Buscar projeto para consultar documentos" value={search} onChange={event => setSearch(event.target.value)} disabled={disabled} />{projects?.map(project => <Button size="sm" variant="outline" key={project.id} onClick={() => setProjectId(project.id)}>{project.projectCode ?? "Projeto"} · {project.producerName}</Button>)}{projects?.length === 0 ? <p>Nenhum projeto encontrado.</p> : null}</> : <>{!currentProjectId ? <Button size="sm" variant="ghost" disabled={disabled} onClick={() => setProjectId(undefined)}>Trocar projeto</Button> : null}<AssistantProposalBoundary key={projectId}><ProjectDocuments projectId={projectId} disabled={disabled} onChange={onChange} /></AssistantProposalBoundary></>}</>}<Button size="sm" variant="outline" disabled={disabled} aria-expanded={draftOpen} onClick={() => setDraftOpen(!draftOpen)}>Preparar custeio a partir de documentos</Button>{draftOpen ? <CusteioDocumentDraft organizationSlug={organizationSlug} /> : null}</section> : null;
    $[17] = currentProjectId;
    $[18] = disabled;
    $[19] = draftOpen;
    $[20] = enabled;
    $[21] = onChange;
    $[22] = open;
    $[23] = organizationSlug;
    $[24] = projectId;
    $[25] = projects;
    $[26] = search;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== t6 || $[29] !== t7) {
    t8 = <div className="space-y-2">{t6}{t7}</div>;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  return t8;
}
function ProjectDocuments(t0) {
  const $ = _c(77);
  const {
    projectId,
    disabled,
    onChange
  } = t0;
  const {
    canQuery
  } = useConvexAuthReady();
  let t1;
  if ($[0] !== canQuery || $[1] !== projectId) {
    t1 = canQuery ? {
      projectId
    } : "skip";
    $[0] = canQuery;
    $[1] = projectId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      initialNumItems: 10
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    results,
    status,
    loadMore
  } = usePaginatedQuery(api.projectDocuments.queries.listProjectDocuments, t1, t2);
  let t3;
  if ($[4] !== canQuery || $[5] !== projectId) {
    t3 = canQuery ? {
      projectId
    } : "skip";
    $[4] = canQuery;
    $[5] = projectId;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const permissions = useQuery(api.projectDocuments.queries.getDocumentPermissions, t3);
  const upload = useProjectDocumentUpload(projectId);
  const fileInput = useRef(null);
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {};
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const [selected, setSelected] = useState(t4);
  const [consentedScope, setConsentedScope] = useState(null);
  const [consistency, setConsistency] = useState(false);
  let t5;
  if ($[8] !== results) {
    t5 = results.filter(_temp);
    $[8] = results;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const eligible = t5;
  let count;
  let signature;
  let t6;
  if ($[10] !== consistency || $[11] !== eligible || $[12] !== selected) {
    signature = eligible.map(_temp2).join();
    let t7;
    if ($[16] !== selected) {
      t7 = Object.keys(selected);
      $[16] = selected;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    count = t7.length;
    t6 = JSON.stringify({
      selected,
      consistency,
      signature
    });
    $[10] = consistency;
    $[11] = eligible;
    $[12] = selected;
    $[13] = count;
    $[14] = signature;
    $[15] = t6;
  } else {
    count = $[13];
    signature = $[14];
    t6 = $[15];
  }
  const consentScope = t6;
  const consent = consentedScope === consentScope;
  let t7;
  if ($[18] !== consent || $[19] !== consistency || $[20] !== count || $[21] !== eligible || $[22] !== onChange || $[23] !== projectId || $[24] !== selected || $[25] !== upload.state) {
    t7 = () => {
      const selections = Object.entries(selected).map(_temp3);
      const parsed = documentChatRequest.safeParse({
        projectId,
        selections,
        consent,
        mode: consistency ? "consistency" : "question"
      });
      const available = selections.every(selection => eligible.some(doc_1 => doc_1._id === selection.documentId));
      onChange({
        request: parsed.success && available ? parsed.data : undefined,
        blocked: count > 0 && (!parsed.success || !available) || upload.state !== null && upload.state.phase !== "failed"
      });
    };
    $[18] = consent;
    $[19] = consistency;
    $[20] = count;
    $[21] = eligible;
    $[22] = onChange;
    $[23] = projectId;
    $[24] = selected;
    $[25] = upload.state;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== consent || $[28] !== consistency || $[29] !== count || $[30] !== onChange || $[31] !== projectId || $[32] !== selected || $[33] !== signature || $[34] !== upload.state) {
    t8 = [projectId, selected, consent, consistency, signature, count, upload.state, onChange];
    $[27] = consent;
    $[28] = consistency;
    $[29] = count;
    $[30] = onChange;
    $[31] = projectId;
    $[32] = selected;
    $[33] = signature;
    $[34] = upload.state;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[36] !== onChange) {
    t9 = () => () => onChange({
      blocked: false
    });
    t10 = [onChange];
    $[36] = onChange;
    $[37] = t10;
    $[38] = t9;
  } else {
    t10 = $[37];
    t9 = $[38];
  }
  useEffect(t9, t10);
  let t11;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <p>Selecione até três PDFs ou arquivos de texto. A pergunta será respondida com citações desses documentos.</p>;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== status) {
    t12 = status === "LoadingFirstPage" ? <p role="status">Carregando documentos...</p> : null;
    $[40] = status;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== eligible.length || $[43] !== status) {
    t13 = status !== "LoadingFirstPage" && eligible.length === 0 ? <p>Nenhum documento disponível nesta página.</p> : null;
    $[42] = eligible.length;
    $[43] = status;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== count || $[46] !== disabled || $[47] !== eligible || $[48] !== selected) {
    let t15;
    if ($[50] !== count || $[51] !== disabled || $[52] !== selected) {
      t15 = doc_2 => <div className="space-y-2" key={doc_2._id}><div className="flex items-center gap-2"><Checkbox id={`chat-doc-${doc_2._id}`} checked={doc_2._id in selected} disabled={disabled || !(doc_2._id in selected) && count >= 3} onCheckedChange={checked => setSelected(current => {
            const next = {
              ...current
            };
            if (checked) {
              next[doc_2._id] = {
                start: "",
                end: ""
              };
            } else {
              delete next[doc_2._id];
            }
            return next;
          })} /><Label htmlFor={`chat-doc-${doc_2._id}`}>{doc_2.title}</Label></div>{selected[doc_2._id] ? <div className="grid grid-cols-1 gap-2 sm:grid-cols-2"><Input label={doc_2.mimeType === "application/pdf" ? "P\xE1gina inicial" : "Linha inicial"} type="number" min={1} isOptional={true} value={selected[doc_2._id]?.start ?? ""} disabled={disabled} onChange={event => setSelected(current_0 => ({
            ...current_0,
            [doc_2._id]: {
              start: event.target.value,
              end: current_0[doc_2._id]?.end ?? ""
            }
          }))} /><Input label={doc_2.mimeType === "application/pdf" ? "P\xE1gina final" : "Linha final"} type="number" min={1} isOptional={true} value={selected[doc_2._id]?.end ?? ""} disabled={disabled} onChange={event_0 => setSelected(current_1 => ({
            ...current_1,
            [doc_2._id]: {
              start: current_1[doc_2._id]?.start ?? "",
              end: event_0.target.value
            }
          }))} /></div> : null}</div>;
      $[50] = count;
      $[51] = disabled;
      $[52] = selected;
      $[53] = t15;
    } else {
      t15 = $[53];
    }
    t14 = eligible.map(t15);
    $[45] = count;
    $[46] = disabled;
    $[47] = eligible;
    $[48] = selected;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  let t15;
  if ($[54] !== loadMore || $[55] !== status) {
    t15 = status === "CanLoadMore" ? <Button size="sm" variant="ghost" onClick={() => loadMore(10)}>Mais documentos</Button> : null;
    $[54] = loadMore;
    $[55] = status;
    $[56] = t15;
  } else {
    t15 = $[56];
  }
  let t16;
  if ($[57] !== disabled || $[58] !== permissions?.canWrite || $[59] !== upload) {
    t16 = permissions?.canWrite ? <><input ref={fileInput} type="file" accept="application/pdf,text/plain,.pdf,.txt" className="hidden" aria-label="Enviar documento ao projeto" onChange={event_1 => {
        const file = event_1.target.files?.[0];
        if (file) {
          upload.start(file);
        }
        event_1.target.value = "";
      }} /><Button size="sm" variant="outline" disabled={disabled || upload.state !== null} onClick={() => fileInput.current?.click()}><FilePlusIcon />Enviar ao projeto</Button></> : null;
    $[57] = disabled;
    $[58] = permissions?.canWrite;
    $[59] = upload;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  let t17;
  if ($[61] !== upload) {
    t17 = upload.state ? <div role="status"><p>{upload.state.name}:{" "}{upload.state.phase === "failed" ? upload.state.error : `${upload.state.progress}%`}</p><Button size="sm" variant="ghost" onClick={() => void upload.cancel()}><XIcon />Cancelar envio</Button>{upload.state.phase === "failed" && upload.state.retryable ? <Button size="sm" variant="outline" onClick={() => void upload.retry()}>Tentar novamente</Button> : null}</div> : null;
    $[61] = upload;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  let t18;
  if ($[63] !== consent || $[64] !== consentScope || $[65] !== consistency || $[66] !== count || $[67] !== disabled) {
    t18 = count > 0 ? <><div className="flex items-center gap-2"><Checkbox id="chat-doc-consistency" checked={consistency} disabled={disabled} onCheckedChange={setConsistency} /><Label htmlFor="chat-doc-consistency">Verificar coerência com o projeto</Label></div><div className="flex items-start gap-2"><Checkbox id="chat-doc-consent" checked={consent} disabled={disabled} onCheckedChange={checked_0 => setConsentedScope(checked_0 ? consentScope : null)} /><Label htmlFor="chat-doc-consent">Autorizo enviar os trechos selecionados ao modelo de IA para esta consulta.</Label></div>{!consent ? <p>Autorize a consulta ou desmarque os documentos para enviar uma mensagem comum.</p> : null}</> : null;
    $[63] = consent;
    $[64] = consentScope;
    $[65] = consistency;
    $[66] = count;
    $[67] = disabled;
    $[68] = t18;
  } else {
    t18 = $[68];
  }
  let t19;
  if ($[69] !== t12 || $[70] !== t13 || $[71] !== t14 || $[72] !== t15 || $[73] !== t16 || $[74] !== t17 || $[75] !== t18) {
    t19 = <div className="space-y-3">{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t18}</div>;
    $[69] = t12;
    $[70] = t13;
    $[71] = t14;
    $[72] = t15;
    $[73] = t16;
    $[74] = t17;
    $[75] = t18;
    $[76] = t19;
  } else {
    t19 = $[76];
  }
  return t19;
}
function _temp3(t0) {
  const [documentId, range] = t0;
  return {
    documentId,
    ...(range.start ? {
      start: Number(range.start)
    } : {}),
    ...(range.end ? {
      end: Number(range.end)
    } : {})
  };
}
function _temp2(doc_0) {
  return `${doc_0._id}:${doc_0.contentVersion}`;
}
function _temp(doc) {
  return doc.assistantEligible;
}